import { governanceApiRef } from '@agilelab/plugin-wb-governance';
import { MetricResult, Timing } from '@agilelab/plugin-wb-governance-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  createContext,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import { witboostSearchApiRef } from '@agilelab/plugin-wb-search';
import {
  CollatorType,
  ComplexFilter,
  JsonObjectSearchFilterVisitor,
  MARKETPLACE_PROJECTS_ENVIRONMENT,
  QueryOperator,
  WitboostMarketplaceSearchResultSet,
  AtomicFilter,
  MARKETPLACE_PROJECTS_URN,
  FilterOperator,
} from '@agilelab/plugin-wb-search-common';
import {
  getDisplayNameMap,
  useDisplayNameConfig,
} from '../../../hooks/useDisplayNameField';
import { useApolloClient } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';
import {
  parseTypeAndKindString,
  resolveTypeDisplayName,
  stringifyTypeAndKind,
} from '../../Search/utils';
import { practiceShaperApiRef } from '@agilelab/plugin-wb-practice-shaper';
import { useDrawersContext } from '@agilelab/plugin-wb-platform';
import { extractIds } from '../../../utils';
import {
  PolicyViolationsResult,
  useGetPolicyViolationsCountMapById,
} from '../../../hooks/useGetPolicyViolationByIdHook';
import { Entity } from '../../Entity';

type ResourceInfo = {
  externalId: string;
  environment: string;
  id: string;
  name: string;
};

interface FlagAndScoreContextProps {
  resourceInfo: ResourceInfo;
  children?: ReactNode;
}

interface FlagAndScoreContextType {
  resourceInfo: ResourceInfo;
  isLoading: boolean;
  environment: string;
  selectedTab: TabEnum;
  setSelectedTab: Dispatch<SetStateAction<TabEnum>>;
  metricResults?: MetricResult[];
  policyViolationsCountMap?: PolicyViolationsResult;
  root: Entity;
  descendants: Map<string, Entity>;
}

const FlagAndScoreContext = createContext<FlagAndScoreContextType>(
  {} as FlagAndScoreContextType,
);

export enum TabEnum {
  Policies,
  Metrics,
}

export const FlagAndScoreContextProvider: FC<FlagAndScoreContextProps> = ({
  resourceInfo,
  children,
}) => {
  const { isOpen } = useDrawersContext();
  const isOpenDrawer = useMemo(() => isOpen(0) ?? false, [isOpen]);
  const identityApi = useApi(identityApiRef);
  const governanceApi = useApi(governanceApiRef);
  const apolloClient = useApolloClient();
  const practiceShaperApi = useApi(practiceShaperApiRef);
  const searchApi = useApi(witboostSearchApiRef);
  const [selectedTab, setSelectedTab] = useState<TabEnum>(TabEnum.Policies);

  const displayNameConfig = useDisplayNameConfig();

  const environment = resourceInfo.environment;

  const { data: policyViolationsCountMap } = useGetPolicyViolationsCountMapById(
    resourceInfo?.externalId,
    resourceInfo?.environment,
  );

  const elementsIds = useMemo(() => {
    if (!policyViolationsCountMap?.descendants?.length) {
      return [];
    }
    return [
      resourceInfo.externalId,
      ...extractIds(policyViolationsCountMap.descendants),
    ];
  }, [policyViolationsCountMap?.descendants, resourceInfo.externalId]);

  // we enrich the entities data leveraging the search api
  const { data: tree, isLoading } = useQuery(
    ['flag-and-score-policy-navigation-data', elementsIds],
    async () => {
      const resolvedTypeMap = new Map<string, string>();

      const types = new Set<string>();

      const results: any[] = [];
      let cursor: string | undefined;

      const environmentFilter = new AtomicFilter(
        MARKETPLACE_PROJECTS_ENVIRONMENT.field,
        FilterOperator.EQ,
        environment,
      );

      const idFilter =
        elementsIds.length === 1
          ? new AtomicFilter(
              MARKETPLACE_PROJECTS_URN.field,
              FilterOperator.EQ,
              elementsIds[0],
            )
          : new AtomicFilter(
              MARKETPLACE_PROJECTS_URN.field,
              FilterOperator.IN,
              elementsIds,
            );

      const filters = new ComplexFilter(QueryOperator.AND, [
        environmentFilter,
        idFilter,
      ]).accept(new JsonObjectSearchFilterVisitor());

      do {
        const response = (await searchApi.queryPost({
          term: '',
          pageCursor: cursor,
          // we select only the fields we need
          select: [
            'type',
            'external_id',
            'document._computedInfo',
            'document.useCaseTemplateId',
            'document.kind',
          ],
          pageLimit: 1000,
          filters,
          types: [CollatorType.MARKETPLACE_PROJECTS],
        })) as WitboostMarketplaceSearchResultSet;

        cursor = response.nextPageCursor;
        results.push(...response.results);
      } while (cursor);

      results?.forEach(r => {
        types.add(
          stringifyTypeAndKind(
            r.document.kind ?? 'Unknown',
            r.document._computedInfo.kind,
          ),
        );
      });

      const typePromises = Array.from(types).map(async t => {
        const { kind, type } = parseTypeAndKindString(t);
        const resolvedType = await resolveTypeDisplayName(
          kind,
          type,
          practiceShaperApi,
        );
        resolvedTypeMap.set(t, resolvedType);
      });

      await Promise.allSettled([...typePromises]);

      const ids = results?.map(r => r.external_id);

      const displayNameMap = await getDisplayNameMap(
        ids,
        apolloClient,
        displayNameConfig!,
      );

      const resultMap = new Map<string, Entity>(
        results.map(searchResult => {
          const { document: doc, external_id } = searchResult;
          const externalId = doc._computedInfo?.urn;

          const displayName = displayNameMap.get(external_id) ?? doc.title;

          return [
            externalId,
            {
              _resolvedType:
                resolvedTypeMap.get(
                  stringifyTypeAndKind(doc.kind, doc._computedInfo.kind),
                ) ?? 'Unavailable',
              externalId,
              id: external_id,
              displayName,
              kind: doc._computedInfo.kind,
              useCaseTemplateId: doc.useCaseTemplateId,
              taxonomyId: doc._computedInfo.taxonomy.external_id,
            },
          ];
        }),
      );

      return resultMap;
    },
    {
      // only run the query when the drawer is open and we have elements to fetch
      enabled: isOpenDrawer && !!elementsIds && !!displayNameConfig,
    },
  );

  // root entity is the one corresponding to the resourceInfo
  const root = useMemo(() => {
    const entity = tree?.get(resourceInfo.externalId);

    if (isLoading || !entity) return resourceInfo;

    return entity ?? resourceInfo;
  }, [isLoading, resourceInfo, tree]);

  const descendants = useMemo(() => {
    if (isLoading || !tree) return new Map();

    const entities = new Map(tree);
    entities.delete(resourceInfo.externalId);

    return entities;
  }, [isLoading, resourceInfo.externalId, tree]);

  // Fetch metric results to show on the preview
  const { data: metricResults } = useQuery({
    queryKey: [
      'metric-results',
      resourceInfo?.externalId,
      resourceInfo?.environment,
    ],
    queryFn: async () => {
      if (!resourceInfo?.externalId || !resourceInfo?.environment) {
        throw new Error('Missing resourceInfo externalId or environment');
      }

      const credentials = await identityApi.getCredentials();

      const response = await governanceApi.getMetricResults(
        {
          resource: resourceInfo.externalId,
          environment: resourceInfo.environment,
          timing: Timing.Runtime,
        },
        credentials,
      );

      return response.results;
    },
    enabled: Boolean(resourceInfo?.externalId && resourceInfo?.environment),
  });

  return (
    <FlagAndScoreContext.Provider
      value={{
        resourceInfo,
        selectedTab,
        setSelectedTab,
        environment,
        metricResults,
        policyViolationsCountMap,

        descendants,
        isLoading,
        root,
      }}
    >
      {children}
    </FlagAndScoreContext.Provider>
  );
};

export const useFlagAndScoreContext = () => useContext(FlagAndScoreContext);
